import { nanoid as uuid } from 'nanoid'
import { v4 } from 'uuid'
import { getPath, addDisposer, getParent, getRoot, getSnapshot, Instance, onPatch, types as t } from '@invisr/mobx-state-tree';
import type { SnapshotIn } from '@invisr/mobx-state-tree';
import crudActions from './crud-actions';
import Auditable from './auditable';
import Mapping from './mapping'
import { IMapping } from './types'
import { toJS, transaction, autorun, reaction } from 'mobx';
import DataTransformer from './filter';
import ref from './reference'
import Component from './component'
import { WorkflowProvider } from './workflow'
//unit is rem
const HEIGHT_BETWEEN_STEPS = 6;
const WIDTH_BETWEEN_STEPS = 6;
export const STEP_HEIGHT = 10;
const STEP_WIDTH = 28
const INITIAL_HEIGHT = 2;

const DEFAULT_MAPPING = { key: 'inputs', type: 'object', bindingType: 'value', children: [] }

export type WorkflowStep = Instance<typeof Step>
export type WorkflowCustomAction = Instance<typeof CustomAction>

function partialResolve(m, inputs, state, root) {
  if(!root) root = m
  let newMapping = Mapping.create({ ...m.$treenode.snapshot, children: [] }, { getOrLoadObject: getRoot(m).getOrLoadObject })
  newMapping.update('children', (m.children || []).map(c => partialResolve(c, inputs, state, root)))
  if(m.bindingType && m.bindingType !== 'value' && m.bindingType !== 'function') {
    if(m.parentComponent === root && m.value?.fieldPath?.startsWith('props.inputs')) {
      let [path1, ...rest] = m.value.fieldPath.replace('props.inputs.', '').split('.')
      let input = inputs?.children?.find(c => c.key === path1)
      newMapping.update({ ...input?.$treenode.snapshot || {}, key: newMapping.key, componentMapping: null })
      if(newMapping.bindingType !== 'value') {
        newMapping.update('value', { fieldPath: [newMapping.value?.fieldPath, ...rest].filter(Boolean).join('.') })
      }
    } else {
      newMapping.update({ bindingType: 'value', value: m.toProps })
    }
  }
  return newMapping.$treenode.snapshot
}

const BaseAction = t
  .model('BaseAction', {
    name: t.optional(t.string, ''),
    type: t.optional(t.string, ''),
  })

const UIComponent = t.model('UIComponent', {
  inputs: t.maybeNull(t.late(() => Mapping)),
  compRef: ref(t.late(() => Component), 'components'),
  state: t.frozen({})
}).views(self => ({
  get component() {
    if(self.compRef) {
      let comp
      comp = Component.create(self.compRef.getSnapshotWithNewIds(), { getOrLoadObject: getRoot(self).getOrLoadObject }) 
      comp.setState(self.state)
      return comp
    }
  },
  get wf() {
    let action = getParent(self)
    let step = getParent(action, 2)
    return getParent(step, 2)
  },
  get mapping() {
    if(self.component) {
      let m = partialResolve(self.component.mapping.asObject.run, self.inputs, self.state, self.component)
      return Mapping.create({ type: 'object', children: [m]}, { getOrLoadObject: getRoot(self).getOrLoadObject })
    }
  },
  afterCreate() {
    addDisposer(self, autorun(() => {
      if (self.component) {
        if(!self.inputs) self.update('inputs', { type: 'object', children: [], key: 'inputs' })
        self.update('state', self.component.state)
        const curr = self.inputs.children
        const next = self.component.mapping.asObject.inputs.children
        for(let c in next) {
          if(!curr[c]) {
            self.inputs.update('children', [...self.inputs.$treenode.snapshot.children || [], next[c].$treenode.snapshot])
          }
        }
      }
    }))
    addDisposer(self, reaction(() => ([self.state, getSnapshot(self.inputs)]), () => {
      getParent(self).update('mapping', self.mapping.$treenode.snapshot)
    }))
  }
})).actions(crudActions)


export const CustomAction = BaseAction
  .named('CustomAction')
  .props({
    id: t.optional(t.identifier, () => uuid()),
    ui: t.maybeNull(t.late(() => UIComponent)),
    mapping: t.maybeNull(t.late(() => Mapping)),
  })
  .views(self => ({
    get key() {
      return self.id
    },
    get label() {
      return getParent(self, 2).label + '.' + self.name
    },
    labels() {
      return {
        meta: {
          mapping: self.mapping
        },
        childNodes: []
      }
    }
  }))
  .actions(crudActions)

export const Step = t
  .model('Step', {
    id: t.identifier,
    name: t.optional(t.string, ''),
    nextTrue: t.maybeNull(t.string),
    nextFalse: t.maybeNull(t.string),
    prevFalse: t.maybeNull(t.string),
    prevTrue: t.maybeNull(t.string),
    delay: t.frozen(),
    dataTransformer: t.maybeNull(t.late(() => DataTransformer)),
    actions: t.maybeNull(t.array(CustomAction))
  })
  .volatile(self => ({
    h: STEP_HEIGHT,
    w: STEP_WIDTH,
  }))
  .views((self) => ({
    get key() {
      return self.id
    },
    get label() {
      return self.name
    },
    get row() {
      return getParent(self, 2).positions[self.id]?.row
    },

    get col() {
      return getParent(self, 2).positions[self.id]?.col
    },
    
    getNextStepX() {
      return { 
        id: 'nexttrue-' + self.id, 
        row: self.row, 
        col: self.col + 1, 
        width: self.width, 
        height: self.height, 
        offsetX: self.getOffsetX(self.col + 1), 
        offsetY: self.getOffsetY(self.row), 
        type: 'end' 
      }
    },

    getNextStepY() {
      return { 
        id: 'nextfalse-' + self.id, 
        col: self.col, 
        row: self.row + 1, 
        height: self.height, 
        width: self.width, 
        offsetY: self.getOffsetY(self.row + 1), 
        offsetX: self.getOffsetX(self.col), 
        type: 'end' 
      }
    },

    getOffsetY(row) {
      if(!row) row = this.row
      row = row - 1;
      let offset = INITIAL_HEIGHT
      while (row >= 0) {
        const biggestHeight = getParent(self, 2).getBiggestHeightInRow(row)
        offset += (biggestHeight + HEIGHT_BETWEEN_STEPS)
        row = row - 1
      }
      return offset
    },

    get offsetY() {
      return this.getOffsetY()
    },

    getOffsetX(col) {
      if(!col) col = this.col
      return col * (STEP_WIDTH + WIDTH_BETWEEN_STEPS)
    },

    get offsetX() {
      return this.getOffsetX()
    },
    get height() {
      return self.h
    },

    get width() {
      return STEP_WIDTH 
    },
    get prev() {
      const parent = getParent(self, 2)
      const p = self.prevTrue || self.prevFalse
      if(p) {
        return parent.steps.get(p)
      }
    },

    labels() {
      const vars = []
      for(let a of self.actions) {
        vars.push({
          label: self.name + '.' + a.name,
          key: a.id,
          childNodes: a.labels(),
          type: 'mapping-absolute',
          meta: {
            mapping: a
          }
        })
      }
      return vars
    },

    get availableVars() {
      let prev = self
      let vars = []
      while(prev) {
        vars.push(...prev.labels())
        prev = prev.prev
      }
      vars.push(...getParent(self, 2).vars)
      return vars
    }
  }))
  .actions(crudActions)
  .actions(self => ({
    afterCreate() {
      // if(!self.dataTransformer) {
      //   self.update('dataTransformer', { where: { rules: [] }})
      // }
      if(!self.conditions) {
        self.update('conditions', [[]])
      }
    },
    setDelay(delay) {
      self.delay = delay
    },
    setHeight(h) {
      self.h = h / parseFloat(getComputedStyle(document.documentElement).fontSize)
    },
    setWidth(w) {
      self.w = w
    },
    addAction(component) {
      if (!self.actions) self.update('actions', [])
      const mapping = component?.mapping.asObject.run
      const ui = component && { compRef: component }
      self.actions.push({ 
        type: 'Custom', 
        ui,
        mapping: mapping?.$treenode.snapshot || {
          type: 'object',
          bindingType: 'value',
          children: [{ key: 'run', type: 'func', bindingType: 'function' }]
        }
      })
    },
    removeAction(index) {
      self.actions.splice(index, 1)
    },
    swapActions(firstIndex: number, secondIndex: number) {
      transaction(() => {
        const actions = self.actions.map(action => ({ ...getSnapshot(action), id: action.id }))
        const first = actions.find((_, index) => firstIndex === index)
        const second = actions.find((_, index) => secondIndex === index)

        actions.splice(firstIndex, 1, second)
        actions.splice(secondIndex, 1, first)

        self.actions = actions
      })
    },
  }))

const Signal = t.model('Signal', {
  id: t.identifier,
  name: t.maybeNull(t.string),
  isPublic: t.optional(t.boolean, false),
  mapping: t.maybeNull(t.late(() => Mapping))
})
.views((self) => ({
  get publicUrl() {
    if(typeof window === 'undefined' || !self.isPublic) return
    return window.location.origin + '/api/signal/' + self.id
  }
}))
.actions(crudActions)
.actions((self) => ({
  addPublicSignal() {
    const value = {
      app: getRoot(self).currentAppKey,
      dataSource: getParent(self, 2).id,
      id: self.id
    }
    fetch('/admin/save', {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{ op: 'replace', path:`/publicSignals:${self.id}`, value }])
    })
  },
  removePublicSignal() {
    fetch('/admin/save', {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{ op: 'remove', path:`/publicSignals:${self.id}` }])
    })
  },
  afterCreate() {
    addDisposer(self, onPatch(self, (patch) => {
      if(patch.path === '/isPublic') {
        if(patch.value === true) {
          self.addPublicSignal()
        } else {
          self.removePublicSignal()
        }
      }
    }))
  }
}))

const WorkflowConfigBase = t
  .model('WorkflowConfig', {
    id: t.identifier,
    type: t.optional(t.string, 'Workflow'),
    name: t.optional(t.string, ''),
    startNode: t.maybeNull(t.string),
    steps: t.map(Step),
    inputs: t.maybeNull(t.late(() => Mapping)),
    signals: t.maybeNull(t.map(Signal)),
    state: t.maybeNull(t.late(() => Mapping)),
    setState: t.maybeNull(t.late(() => Mapping))

  })
  .actions(crudActions)
  .actions((self) => ({
    addSignal() {
      self.signals.put({ id: v4(), name: 'New signal', mapping: {
        type: 'func', js: true, bindingType: 'function', children: [
          { key: 'inputs', type: 'object', bindingType: 'data', mappingPath: getPath(self.inputs) },
          { key: 'state', type: 'object', bindingType: 'data', mappingPath: getPath(self.state) },
          { key: 'params', type: 'object', bindingType: 'data', mappingPath: getPath(self) + '/PARAMS' },
        ]
      } })
    }
  }))
  .actions((self) => {
    let dispose

    const createStep = ({
      name,
      type = 'step',
      delay,
      conditions,
      actions,
      nextFalse,
      nextTrue,
      prevTrue = undefined,
      prevFalse = undefined,
      row = 0,
      col = 0
    }: Partial<Omit<WorkflowStep, 'id'>>): WorkflowStep => {
      return {
        id: uuid(),
        name,
        type,
        delay: delay || { unit: 'immediate' },
        conditions: conditions || [],
        actions: actions || [],
        nextFalse,
        nextTrue,
        prevFalse,
        prevTrue,
        row,
        col
      }
    }
    const removeInput = (index) => {
      self.inputs.children.splice(index, 1)
    }
    const addInput = () => {
      self.inputs.children.push({
        key: `p${self.inputs.children.length}`,
        type: 'string',
        //bindingType: 'value'
      })
    }
    const addStep = (step: Partial<Omit<WorkflowStep, 'id'>>) => {
      if (!step.name) step.name = 'Step ' + (self.steps.size + 1)
      const newStep = createStep(step)
      self.steps.put(newStep)
      if (step.prevTrue) {
        const parent = self.steps.get(step.prevTrue)
        if (parent.nextTrue) {
          self.steps.set(newStep.id, { ...self.steps.get(newStep.id), nextTrue: parent.nextTrue })
          self.steps.set(parent.nextTrue, { ...self.steps.get(parent.nextTrue), prevTrue: newStep.id })
        }
        self.steps.set(parent.id, { ...parent, nextTrue: newStep.id })
      }
      if (step.prevFalse) {
        const parent = self.steps.get(step.prevFalse)
        if (parent.nextFalse) {
          self.steps.set(newStep.id, { ...self.steps.get(newStep.id), nextFalse: parent.nextFalse })
          self.steps.set(parent.nextFalse, { ...self.steps.get(parent.nextFalse), prevFalse: newStep.id })
        }
        self.steps.set(parent.id, { ...parent, nextFalse: newStep.id })

      }
      return self.steps.get(newStep.id)
    }

    const deleteStep = (stepId: string) => {
      const step = self.steps.get(stepId)

      const nextTrueStep = self.steps.get(step.nextTrue)
      const nextFalseStep = self.steps.get(step.nextFalse)

      const prev = step.prevTrue || step.prevFalse
      const prevStep = self.steps.get(prev)
      let nextStep = nextTrueStep || nextFalseStep;

      const prevFalse = step.prevFalse
      const prevTrue = step.prevTrue

      step.del()

      if (nextStep) {
        if (prev === prevTrue) {
          if(prevStep) prevStep.nextTrue = nextStep.id
          nextStep.prevTrue = prev
          nextStep.prevFalse = undefined
        }
        else if (prev === prevFalse) {
          if(prevStep) prevStep.nextFalse = nextStep.id
          nextStep.prevFalse = prev
          nextStep.prevTrue = undefined
        }
      }
      else if(prev) {
        if (prev === prevTrue) {
          self.steps.set(prev, { ...prevStep, nextTrue: null })
        }
        else if (prev === prevFalse) {
          self.steps.set(prev, { ...prevStep, nextFalse: null })
        }
      }
    }

    const updateStep = (stepId: string, step: WorkflowStep) => {
      self.steps.set(stepId, step)
    }

    const updateInputKey = (index, key) => {
      self.inputs.children[index].key = key
    }

    const updateInputType = (index, type) => {
      self.inputs.children[index].type = type
    }

    const afterCreate = () => {
      if (!self.inputs) {
        self.update('inputs', DEFAULT_MAPPING)
      }
      if (!self.signals) {
        self.update('signals', {})
      }
      if (!self.state) {
        self.update('state', {...DEFAULT_MAPPING, key: 'state'})
      }
      if (!self.setState) {
        self.update('setState', {...DEFAULT_MAPPING, key: 'setState'})
      }
    }

    const beforeDestroy = () => {
      dispose?.()
    }

    return {
      createStep,
      addStep,
      updateStep,
      deleteStep,
      removeInput,
      addInput,
      updateInputKey,
      updateInputType,
      afterCreate,
      beforeDestroy
    }
  })
  .views((self) => ({
    // get key() {
    //   return self.id
    // },
    get provider() {
      return WorkflowProvider
    },
    get label() {
      return 'State'
    },
    get funcs() {
      return [{
        meta: {
          mapping: self.setState,
          type: 'state'
        },
        type: 'mapping-absolute',
        key: 'setState',
        label: 'setState',
        childNodes: []
      }]
    },    
    get vars() {
      return [{
        meta: {
          mapping: self.inputs
        },
        type: 'mapping-absolute',
        key: 'inputs',
        label: 'Inputs',
        childNodes: self.inputs?.labels().childNodes
      },
      {
        meta: {
          mapping: self.state
        },
        type: 'mapping-absolute',
        key: 'state',
        label: 'State',
        childNodes: []
      }]
    },
    labels() {
      return { 
        label: self.name,
        key: self.id,
        meta: {
          type: 'input'
        },
        childNodes: [...self.vars,
        {
          key: 'methods',
          label: 'Methods',
          childNodes: [
            {
              key: 'run',
              label: 'run',
              secondaryLabel: 'FUNCTION'
            },
            {
              key: 'query',
              label: 'query',
              secondaryLabel: 'FUNCTION'
            },
            {
              key: 'signal',
              label: 'signal',
              secondaryLabel: 'FUNCTION'
            },
            {
              key: 'restart',
              label: 'restart',
              secondaryLabel: 'FUNCTION'
            }
          ]
        }      
      ]}
    },
    get allActionsNames() {
      const names = []
      for (let step of self.steps.values()) {
        for (let action of step.actions) {
          names.push(action.name)
        }
      }
      return names
    },
    get stateNames() {
      return Array.from(self.steps.values()).map((step, i) => ({ name: step.name, id: step.id, index: i }))
    },
    getStep(row: number, col: number) {
      const step = Array.from(self.steps.values()).find((step: WorkflowStep) => step.row === row && step.col === col) as WorkflowStep | undefined
      return step
    },
    getStepById(stepId: string) {
      return self.steps.get(stepId)
    },
    getBiggestHeightInRow(row: number) {
      let max = 0

      const steps = Array.from(self.steps.values()).filter(step => step.row === row)
      steps.forEach(step => {
        if (step.height > max) max = step.height
      })

      return max
    },
    positionsHelperOld(step: WorkflowStep, row: number, col: number, positions: object) {
      let offset = 0

      if (step.nextFalse) {
        //const anotherStep = Object.values(positions).find(pos => row === pos.row && col + 1 === pos.col)

        const nextColumnSteps = Object.values(positions).filter(pos => col + 1 === pos.col)
        const biggestRowOnTheNextColumn = nextColumnSteps.reduce((prev, curr) => Math.max(prev, curr.row), 0)
        //there is another step in the place of step.nextFalse
        if (biggestRowOnTheNextColumn >= row) {
          return this.positionsHelper(step, row + 1, col, positions) + 1
        }

        offset = this.positionsHelper(self.steps.get(step.nextFalse), row, col + 1, positions)
      }

      if (step.nextTrue) {
        this.positionsHelper(self.steps.get(step.nextTrue), row + 1 + offset, col, positions)
      }

      positions[step.id] = { row: row + offset, col }

      return offset
    },

    positionsHelper(step: WorkflowStep, row: number, col: number, positions: object, rowCounts = {}) {
      let offset = 0
      if(!step) return 0
      rowCounts[col] = row + 1
      
      if (step.nextTrue) {
        const nextRows = Object.keys(rowCounts).filter(k => parseInt(k) > col)
        const maxRows = nextRows.reduce((prev, curr) => Math.max(prev, rowCounts[curr]), 0)

        if(maxRows > row) {
          row = maxRows + 1
          positions[step.id] = { row, col }
        }
        this.positionsHelper(self.steps.get(step.nextTrue), row, col + 1, positions, rowCounts)
      }
      
      if (step.nextFalse) {
        this.positionsHelper(self.steps.get(step.nextFalse), row + 1, col, positions, rowCounts)
      }

      positions[step.id] = { row: row, col }        

      return offset
    },

    get totalHeight() {
      let totalHeight = 0
      self.steps.forEach((step) => {
        const h = step.offsetY + step.height
        if (h > totalHeight) totalHeight = h
      })
      return totalHeight
    },

    get totalWidth() {
      let totalWidth = 0
      self.steps.forEach((step) => {
        const w = step.offsetX + step.width
        if (w > totalWidth) totalWidth = w
      })
      return totalWidth
    },

    get start() {
      return self.steps.get(self.startNode)
    },

    get positions() {
      const positions = {}
      if(self.start) {
        this.positionsHelper(self.start, 0, 0, positions)
      }
      return positions
    },
    
    get arrows() {
      return Array.from(self.steps.values()).flatMap(step => {
        let nextTrue = self.steps.get(step.nextTrue)
        let nextFalse = self.steps.get(step.nextFalse)

        nextTrue = nextTrue || step.getNextStepX() 
        nextFalse = nextFalse || step.getNextStepY()
        let arrows = []
        if (nextTrue) {
          arrows.push({ from: step, to: nextTrue })
        }
        if (nextFalse) {
          arrows.push({ from: step, to: nextFalse })
        }

        return arrows
      })
    },
    async fetchExecutions(nextPageToken: any) {
      let result = {};
      try {
        let url: string = `/api/workflow/allWorkflows?dataSourceKey=${(self as any).key}`;
        if (nextPageToken) url += `&nextPageToken=${nextPageToken}`;
        result = await fetch(url).then(r => r.json());
      } catch (e) {
        console.error('Fetch executions of workflow failed with error: ', e);
      }
      return result;
    }

  }))

interface OperationMetaData {
  dataSourceType: string,
  dataSourceKey: string,
  dataSourceId: string,
  dataSourceMethodName: string,
  inputs: IMapping
}

export type IWorkflowConfig = Instance<typeof WorkflowConfigBase>
export type IWorkflowConfigSnapshot = SnapshotIn<typeof WorkflowConfigBase>

const IWFConfig = t.compose('WorkflowConfig', WorkflowConfigBase, Auditable);
export default IWFConfig;
export type IWFConfigSnapshot = SnapshotIn<typeof IWFConfig>

